// ---------------------------------------------------------------------------
// ProgressDrawerHeader — header do drawer "Progresso da modelagem".
// ---------------------------------------------------------------------------
// Mesmo esquema visual do ``DrawerHeader`` do drawer do input: fundo
// escuro (#353534), strip vertical na cor do cliente, titulo branco.
// As diferencas:
//   - subtitulo descreve a IMPLEMENTACAO (nao um input);
//   - "period pill" e clicavel — abre um menu com os meses
//     disponiveis no ``monthlyStatus``. Selecionar troca o mes
//     corrente da view (callback ``onSelectMonth(idx)`` repassado
//     pelo ``InputsV3``).
//
// Decisao da sub-leva A: o seletor de mes vive AQUI dentro do drawer.
// O modal antigo recebia ``selectedMonthLabel`` apenas como string
// (sem interatividade); para virar drawer faz mais sentido o usuario
// poder navegar entre meses sem ter que fechar o drawer e clicar na
// barra horizontal de meses.
//
// TO-DO (sub-leva A): replicar essa logica de seletor de mes dentro
// do ``InputDrawerV3`` (drawer do input). Hoje o drawer do input
// recebe ``period`` como string fixa; faz sentido permitir trocar de
// mes diretamente la tambem, espelhando o comportamento aqui.
// ---------------------------------------------------------------------------

import React, { useState } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import Menu from "@material-ui/core/Menu";
import MenuItem from "@material-ui/core/MenuItem";
import ArrowDropDown from "@material-ui/icons/ArrowDropDown";
import ChevronLeft from "@material-ui/icons/ChevronLeft";
import ChevronRight from "@material-ui/icons/ChevronRight";
import Close from "@material-ui/icons/Close";

import useDrawerStyles from "../drawer/styles";
import useThemeColors from "../../../../hooks/useThemeColors";
import useProgressStyles from "./styles";
import { monthStatusColor } from "../../../../services/inputsV3StateRules";

// Onda 1.3: paleta unificada via ``monthStatusColor`` (importado de
// ``inputsV3StateRules.js``). Antes este arquivo tinha o seu proprio
// ``MONTH_STATUS_COLOR`` que esperava o vocabulario V2
// (``done``/``action``/``pending``/``waiting``/``stale``) — porem o
// backend V3 emite vocabulario diferente (``complete``/``action_needed``/
// ``ready_to_calculate``/...), entao TODA resposta caia no fallback
// ``waiting`` (cinza) e a bolinha de status no menu de meses nunca
// refletia o estado real. ``monthStatusColor`` aceita ambos
// vocabularios via ``classifyMonthAggregate`` interno.
const statusColor = monthStatusColor;

const ProgressDrawerHeader = ({
  monthlyStatus,
  selectedMonthIndex,
  onSelectMonth,
  onClose,
}) => {
  const { t } = useTranslation();
  const { clientColor } = useThemeColors();
  const classesDrawer = useDrawerStyles({ clientColor });
  const classes = useProgressStyles();

  const [menuAnchor, setMenuAnchor] = useState(null);
  const selected = monthlyStatus && monthlyStatus[selectedMonthIndex];
  const hasMonths = Array.isArray(monthlyStatus) && monthlyStatus.length > 0;

  const handleOpenMenu = (e) => {
    if (!hasMonths) return;
    setMenuAnchor(e.currentTarget);
  };
  const handleClose = () => setMenuAnchor(null);
  const handlePick = (idx) => {
    handleClose();
    if (onSelectMonth) onSelectMonth(idx);
  };

  // Navegacao 1-mes-por-vez via chevrons. Disabled nos extremos.
  const canPrev = hasMonths && selectedMonthIndex > 0;
  const canNext = hasMonths && selectedMonthIndex < monthlyStatus.length - 1;
  const goPrev = () =>
    canPrev && onSelectMonth && onSelectMonth(selectedMonthIndex - 1);
  const goNext = () =>
    canNext && onSelectMonth && onSelectMonth(selectedMonthIndex + 1);

  return (
    <div className={classesDrawer.drawerHeader}>
      <div className={classesDrawer.clientStrip} />
      <div className={classesDrawer.headerMain}>
        <h3 className={classesDrawer.headerTitle}>Progresso da modelagem</h3>
        <div className={classesDrawer.drawerSubtitle}>
          <span>Cálculos</span>
        </div>
      </div>

      <div className={classes.monthSelectorGroup}>
        <button
          type="button"
          className={classes.monthChevron}
          onClick={goPrev}
          disabled={!canPrev}
          aria-label="Mês anterior"
          title="Mês anterior"
        >
          <ChevronLeft />
        </button>
        <button
          type="button"
          className={classes.monthSelectorBtn}
          onClick={handleOpenMenu}
          disabled={!hasMonths}
          title={hasMonths ? "Trocar de mês" : "Sem períodos disponíveis"}
        >
          {selected
            ? `${t("months." + selected.month.toString())} · ${selected.year}`
            : "—"}
          <ArrowDropDown />
        </button>
        <button
          type="button"
          className={classes.monthChevron}
          onClick={goNext}
          disabled={!canNext}
          aria-label="Próximo mês"
          title="Próximo mês"
        >
          <ChevronRight />
        </button>
      </div>

      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleClose}
        className={classes.monthSelectorMenu}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        getContentAnchorEl={null}
      >
        {(monthlyStatus || []).map((m, idx) => (
          <MenuItem
            key={`${m.year}-${m.month}`}
            className={classes.monthMenuItem}
            selected={idx === selectedMonthIndex}
            onClick={() => handlePick(idx)}
          >
            <span
              className="month-status-dot"
              style={{ background: statusColor(m.status) }}
            />
            <span>
              {t("months." + m.month.toString())} {m.year}
            </span>
          </MenuItem>
        ))}
      </Menu>

      <button
        type="button"
        className={classesDrawer.drawerClose}
        onClick={onClose}
        aria-label="Fechar"
      >
        <Close fontSize="small" />
      </button>
    </div>
  );
};

ProgressDrawerHeader.propTypes = {
  monthlyStatus: PropTypes.array,
  selectedMonthIndex: PropTypes.number,
  onSelectMonth: PropTypes.func,
  onClose: PropTypes.func.isRequired,
};

export default ProgressDrawerHeader;
